/* eslint-disable react/require-default-props */
/* eslint-disable react/prop-types */
/* eslint-env browser */
import React from 'react'

import classNames from 'classnames'
import slug from 'slug'
import { useTranslator } from '../TranslatorProvider'
import { HydroCard } from './HydroCard'

const OneCatch = require('../CatchesGrid/CatchItem')

interface FishingTrail {
  id: string
  type?: string
  name: string
  description?: string
  websiteUrl?: string
  facebookUrl?: string
  contactPhone?: string
  contactEmail?: string
  trailType?: string
  isFloatTubeAllowed?: boolean
  isNoKill?: boolean
  isNightFishingAllowed?: boolean
  isFlyFishing?: boolean
  dimensions?: number
  topographyType?: string
  frenchCategory?: string
  regulations?: string
  hasWifi?: boolean
  isAnimalAllowed?: boolean
  hasSanitary?: boolean
  isPrivate?: boolean
  isPaying?: boolean
  price?: number
  geometryPoint?: any
  geometryPoint3857?: any
  place?: any
}

interface FishingTrailInfosProps {
  host: string
  hostPath: string
  locale: string
  fishCatches: any[]
  appConfig: any
  nearFishingTrails: FishingTrail[]
  fishingTrail: FishingTrail
}

interface AttributeLineProps {
  labelKey: string
  value?: string
  boolValue?: boolean
  valueKeyPrefix?: string
  t: (key: string) => string
  inline?: boolean
}
const AttributeLine = ({ labelKey, value, boolValue, valueKeyPrefix, t, inline = false }: AttributeLineProps) => {
  let valueToDisplay = value
  if (valueKeyPrefix && value) {
    valueToDisplay = t(`${valueKeyPrefix}.${value}`)
  } else if (typeof boolValue !== 'undefined' && boolValue !== null) {
    valueToDisplay = t(`hydro.fishingTrails.attributes.${labelKey}.${boolValue ? 'yes' : 'no'}`)
  }

  if (!valueToDisplay) {
    return null
  }

  return (
    <div
      className={inline ? '' : 'has-padding-bottom-small'}
      style={{
        paddingBottom: inline ? 4 : undefined,
      }}
    >
      <span className="has-text-weight-normal">{t(`hydro.fishingTrails.attributes.${labelKey}.title`)} </span>
      {!inline && <br />}
      <span className="has-text-weight-bold">{valueToDisplay}</span>
    </div>
  )
}

export const FishingTrailInfos: React.FC<FishingTrailInfosProps> = ({
  fishingTrail,
  fishCatches,
  nearFishingTrails,
  locale,
  appConfig,
  host,
  hostPath,
}) => {
  const t = useTranslator(locale)

  return (
    <div>
      <div className="columns is-multiline has-padding-horizontal-small-mobile has-padding-horizontal-medium-tablet">
        <div className="column is-full ">
          {fishingTrail.type && (
            <div className="title is-3">
              <span>{t(`hydro.fishingTrails.types.${fishingTrail.type}`)}</span>
            </div>
          )}
          {fishingTrail.description && (
            <div className="content">
              <span>{fishingTrail.description}</span>
            </div>
          )}
        </div>
        <div className="column is-half-desktop is-full">
          {fishingTrail.contactEmail && (
            <div className="has-padding-bottom-small">
              <a href={`mailto:${fishingTrail.contactEmail}`}>{fishingTrail.contactEmail}</a>
            </div>
          )}
          {fishingTrail.contactPhone && (
            <div className="has-padding-bottom-small">
              <a href={`tel:${fishingTrail.contactPhone}`}>{fishingTrail.contactPhone}</a>
            </div>
          )}
          {fishingTrail.websiteUrl && (
            <div>
              <a href={`${fishingTrail.websiteUrl}?ref=fishfriender`} target="_blank" rel="noopener noreferrer">
                <i className="far fa-external-link">&nbsp;</i>&nbsp;
                {fishingTrail.websiteUrl.replace(new RegExp('/$'), '')}
              </a>
            </div>
          )}
          {fishingTrail.facebookUrl && (
            <div>
              <a href={fishingTrail.facebookUrl} target="_blank" rel="noopener noreferrer">
                <i className="far fa-external-link">&nbsp;</i>&nbsp;
                {fishingTrail.facebookUrl.replace(new RegExp('/$'), '')}
              </a>
            </div>
          )}
          <AttributeLine
            t={t}
            labelKey="frenchCategory"
            value={fishingTrail.frenchCategory}
            valueKeyPrefix="hydro.fishingTrails.frenchCategory"
          />
          <AttributeLine
            t={t}
            labelKey="trailTypes"
            value={fishingTrail.trailType}
            valueKeyPrefix="hydro.fishingTrails.trailTypes"
          />
          <AttributeLine t={t} labelKey="regulations" value={fishingTrail.regulations} />
        </div>
        <div className="column is-half-desktop is-full">
          <div>
            <AttributeLine t={t} labelKey="isFloatTubeAllowed" boolValue={fishingTrail.isFloatTubeAllowed} inline />
            <AttributeLine t={t} labelKey="isNoKill" boolValue={fishingTrail.isNoKill} inline />
            <AttributeLine
              t={t}
              labelKey="isNightFishingAllowed"
              boolValue={fishingTrail.isNightFishingAllowed}
              inline
            />
            <AttributeLine t={t} labelKey="isFlyFishing" boolValue={fishingTrail.isFlyFishing} inline />
            <AttributeLine t={t} labelKey="hasWifi" boolValue={fishingTrail.hasWifi} inline />
            <AttributeLine t={t} labelKey="isAnimalAllowed" boolValue={fishingTrail.isAnimalAllowed} inline />
            <AttributeLine t={t} labelKey="hasSanitary" boolValue={fishingTrail.hasSanitary} inline />
            <AttributeLine t={t} labelKey="isPrivate" boolValue={fishingTrail.isPrivate} inline />
            <AttributeLine t={t} labelKey="isPaying" boolValue={fishingTrail.isPaying} inline />
            {fishingTrail.isPaying && fishingTrail.price && (
              <AttributeLine t={t} labelKey="price" value={`${fishingTrail.price.toString()} €`} inline />
            )}
          </div>
        </div>
        {nearFishingTrails.length > 0 && (
          <>
            <div className="column is-12">
              <hr />
              <h3 className="title is-4 has-padding-top-medium">{t('hydro.fishingTrails.nearbyTitle')}</h3>
            </div>
            <div className="column is-12">
              <div className="columns is-multiline">
                {nearFishingTrails.map(elt => (
                  <HydroCard
                    key={`${elt.id}-${elt.name}`}
                    imgAlt={elt.name}
                    title={elt.name}
                    subtitle={elt.trailType && t(`hydro.fishingTrails.trailTypes.${elt.trailType}`)}
                    href={`/spots/${slug(elt.name)}-${elt.id}`}
                    icon="fa-map-pin"
                  />
                ))}
              </div>
            </div>
          </>
        )}
        {fishCatches.length > 0 && (
          <div className="column is-full">
            <hr />
            <h3 className="title is-4 has-padding-top-medium">{t('hydro.fishingTrails.catchesTitle')}</h3>
            <div className={classNames('columns is-mobile is-variable is-12 is-multiline')}>
              {fishCatches.slice(0, 12).map((elt: any) => (
                <div className={classNames('column', 'is-6-mobile is-4-tablet is-3-desktop')} key={elt.id}>
                  <OneCatch fishCatch={elt} actor={elt.creator} locale={locale} displayLikes={false} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
